// //StreamCryptoCInfo.js

import React, { useEffect, useState, useContext, useCallback } from 'react';
import { w3cwebsocket as W3CWebSocket } from "websocket";
import { useDispatch, useSelector } from 'react-redux';
import styles from './StreamCryptoCInfo.module.css'
import Spinner from '../components/Spinner';
import EmaSelector from '../components/EmaSelector';
import LimitedAdvise from '../components/LimitedAdvise';
import { useNavigate } from 'react-router-dom';
import { CsrfTokenContext } from '../CsrfTokenContext';
import { setPrices, setInitialPrices, setSymbols, setSymbolIcons, setSymbolsInfo, setChanges, setFinishLoad, setSpinList, setSymbolList, setSelectedSymbol, setSelectedRowIndex } from '../pages/slices/streamCryptoInfoSlicer';
import { isTokenExpired, handleLogin } from '../pages/utils/AuthenticationHelper';

const binanceWebSocketURL = `wss://data-stream.binance.vision:9443/ws/stream`;
// const binanceWebSocketURL = `wss://stream.binance.com:9443/ws/stream`;
const StreamCryptoCInfo = () => {

  const csrfToken = useContext(CsrfTokenContext);  
  const [newPrices, setNewPrices] = useState({});
  const [defaultPrices, setdefaultPrices] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const dispatch = useDispatch();  
  const navigate = useNavigate();  
  const { prices, initialPrices, symbols, symbolIcons, symbolsInfo, finishedLoad, spinList, symbolList, selectedRowIndex } = useSelector(state => state.streamCryptoInfo);
  const { isPremium, symbolLimit } = useSelector(state => state.globalData);  

  const refreshTokenIfNeeded = useCallback(async () => {
    if (isTokenExpired()) {        
      await handleLogin();
    }    
  },[]);

  useEffect(() => {

    const fetchSymbols = async () => {
        try { 

          await refreshTokenIfNeeded();
            
          dispatch(setSymbolList([]));
                    
          const response = await fetch(`/api/symbols?limit=${symbolLimit}`, {
              method: 'GET',
              // credentials: 'include',
              headers: {                
                  'Content-Type': 'application/json',
                  'X-CSRFToken': csrfToken
              }
          });
          
          if (!response.ok) {
              const errorData = await response.json();
                  throw new Error(errorData.error);
          }
          const result = await response.json();
          await Promise.all(result);
          
          dispatch(setSymbolList(result));
            
        } catch (err) {
            console.error('Error fetching data:', err);            
        }
     };

     fetchSymbols();

  }, [symbolLimit, dispatch, csrfToken, refreshTokenIfNeeded]);

  useEffect(() => {
    dispatch(setPrices(newPrices));      
  }, [newPrices, dispatch]);

  useEffect(() => {    
    dispatch(setInitialPrices(defaultPrices));
  }, [defaultPrices, dispatch]);

  useEffect(() => {
    const fetchSymbols = async () => {
      try {

        await refreshTokenIfNeeded();

        dispatch(setSymbolsInfo([]));
        dispatch(setSpinList(true));
        
        const response = await fetch(`/api/crps_info?limit=${symbolLimit}`, {
          method: 'GET',
          // credentials: 'include',
          headers: {            
              'Content-Type': 'application/json',
              'X-CSRFToken': csrfToken
          }
      });
        
        if (!response.ok) {
          const errorData = await response.json();
          dispatch(setSpinList(false));
          throw new Error(errorData.error);
        }
    
        const result = await response.json();        
        dispatch(setSymbolsInfo(result));
        dispatch(setSpinList(false));
    
      } catch (err) {
        console.error('Error fetching data:', err);
        dispatch(setSpinList(false));
      }
    };
  
    fetchSymbols();
  }, [finishedLoad, symbolLimit, dispatch, csrfToken, refreshTokenIfNeeded]); 


  const fetch24hChange = useCallback(async (symbol) => {    
    try {
      const base = symbol.toUpperCase().replace(/USDT$/, '');
      if (!base) return 0;
      const response = await fetch(`/api/binanceRestAPIURL?symbol=${base}USDT`);
      if (!response.ok) return 0;
      const data = await response.json();      
      return parseFloat(data.priceChangePercent).toFixed(2);
    } catch (error) {
      return 0;
    }
  },[]);
  
    
  useEffect(() => {
    const loadSymbols = (limit) => {
      
      // if(symbols.length > 0) return; 
      
      dispatch(setSymbols([]));
      dispatch(setSymbolIcons([]));
      
      const top200Symbols = symbolList
        .map(item => {
          const base = item.symbol_name.split('-')[0].toLowerCase();
          return {
            value: base + 'usdt',
            icon: item.symbol_icon,
            rank: item.ranking
          };
        })
        .filter(item => item.value !== 'usdtusdt');
           
      const topLimit = top200Symbols.slice(0, limit);      
      dispatch(setSymbols(topLimit));     
         
      const symbolIconMapping = topLimit.reduce((acc, item) => {
        acc[item.value.toUpperCase()] = item.icon;
        return acc;
      }, {});  
      
      dispatch(setSymbolIcons(symbolIconMapping));
            
    };
    
    if(symbolLimit === 11 || symbolLimit === 200){
      loadSymbols(symbolLimit);
    }

  }, [symbolList, symbolLimit, dispatch]);

  const getPriceBySymbol = (list, symbol) => {
    const item = list.find(obj => obj.symbol === symbol);
    return item ? parseFloat(parseFloat(item.price).toFixed(7)) : null;
  };

  useEffect(() => {

    if (symbols.length === 0 ||  (!Array.isArray(symbolsInfo)) || symbolsInfo.length === 0) return;    

    const defaultPrices = symbols.slice(0, symbolLimit).reduce((acc, symbol) => {
      const ticker = symbol.value.toUpperCase();      
      const tickerResearch = ticker.slice(0, -4);      
      acc[ticker] = getPriceBySymbol(symbolsInfo, tickerResearch);
      return acc;
    }, {});

    setNewPrices(defaultPrices);
    setdefaultPrices(defaultPrices); 

  }, [symbolsInfo, symbols, symbolLimit]);


  useEffect(() => {
    if (symbols.length === 0) return;
    dispatch(setFinishLoad(false));
    dispatch(setChanges([]));      

    let client;
    let isDestroyed = false;
    let reconnectTimer = null;

    const createWebSocket = () => {
        if (isDestroyed) return;
        client = new W3CWebSocket(binanceWebSocketURL);

        client.onopen = () => {
            if (isDestroyed) { client.close(); return; }
            const subscribeMessage = {
                method: "SUBSCRIBE",
                params: symbols.slice(0, symbolLimit).map(symbol => `${symbol.value}@trade`),
                id: 1
            };
            client.send(JSON.stringify(subscribeMessage));
        };

        client.onmessage = (message) => {
            const data = JSON.parse(message.data);
            if (data.e === "trade") {
                setNewPrices((prevPrices) => {
                    const newPrice = parseFloat(parseFloat(data.p).toFixed(7));
                    const symbol = data.s;

                    if (prevPrices[symbol] === null) {              
                        setdefaultPrices((prevInitialPrices) => ({
                            ...prevInitialPrices,                
                            [symbol.toUpperCase()]: newPrice,
                        }));
                    }       

                    return {
                        ...prevPrices,              
                        [symbol.toUpperCase()]: newPrice,
                    };
                });
            }
        };

        client.onerror = () => {};

        client.onclose = () => {
            if (!isDestroyed) {
                reconnectTimer = setTimeout(() => createWebSocket(), 5000);
            }
        };
    };

    createWebSocket();

    const fetchAllChanges = async (symbols) => {
        const changes = {};
        await Promise.all(symbols.map(async (symbol) => {
            const change = await fetch24hChange(symbol.value);
            changes[symbol.value.toUpperCase()] = parseFloat(change);
        }));
        return changes;
    };

    const updateChanges = async () => {
        const changes = await fetchAllChanges(symbols.slice(0, symbolLimit));
        dispatch(setChanges(changes));
    };
    
    updateChanges();
    dispatch(setFinishLoad(true));

    return () => {
        isDestroyed = true;
        clearTimeout(reconnectTimer);
        if (client && client.readyState !== W3CWebSocket.CLOSED) {
            client.close();
        }
    };
}, [symbols, dispatch, symbolLimit, fetch24hChange]);  // Added symbolLimit as a dependency

  const getPercentageChange = (symbol) => {    
    if (initialPrices[symbol] === null || prices[symbol] === null) {
      return null;
    }
    const initialPrice = parseFloat(initialPrices[symbol]);
    const currentPrice = parseFloat(prices[symbol]);        
    const percentageChange = ((currentPrice - initialPrice) / initialPrice) * 100
    if(isNaN(percentageChange)) return 'isNaN';
    return percentageChange.toFixed(2);
  };
  
  const handleSelection = (idx, item) => { 
    const ticker = item.slice(0, -4) + '-USD';
    dispatch(setSelectedRowIndex(idx));
    dispatch(setSelectedSymbol(ticker));    
    navigate('/TRViwerPage');
  };
  
  const handleSearch = (event) => {
    setSearchQuery(event.target.value.toUpperCase());
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const filteredSymbols = Object.keys(prices).filter((symbol) =>
    symbol.toUpperCase().includes(searchQuery)
  );

  const getColor = (value) => {
    const parsedValue = parseFloat(value);
    if (isNaN(parsedValue)) {
      return '#000000'; // Default color for invalid numbers
    }
    return parsedValue >= 0 ? '#6ac46a' : '#ec8282';
  };

  return (
    <>
      {/* {spinList && (
        <>
          <br />
          <br />
          <Spinner title="" color="#0e440d" />
        </>
      )}     */}
    {/* {!spinList && symbolList.length > 0 && */}
      <><div style={{ color: 'antiquewhite', fontFamily: 'system-ui', border: '2px solid', borderRadius: '10px', borderColor: '#444444' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search symbol"
              value={searchQuery}
              onChange={handleSearch}
              style={{
                padding: '2px',
                fontSize: '12pt',
                backgroundColor: 'transparent',
                border: 'none',
                color: 'white',
                outline: 'none',
                width:'110px'
              }}
            />
            <div onClick={() => clearSearch()}  style={{
              border: '1px solid gray',
              borderRadius: '10px',
              width: '15px',
              height: '15px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: '8px',
              cursor:'pointer'             
            }}>
              x
            </div>
            {!isPremium &&
              <div style={{position:'absolute', right:'8%'}}>
                <LimitedAdvise 
                            width="70px" 
                            height="17px" 
                            message="Limited" 
                            tooltipTitle="Why it's limited?"
                            tooltipPhrases={[
                              "Up to 10 symbols"
                            ]}
                            tooltipPosition="left"  /* Options: top, down, left, right */
                        />
              </div>
            }
          </div>
          <div className={styles.scrollableContainer} style={{ height: '195px', overflowY: 'auto', position: 'relative', borderRadius: '5px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', position: 'sticky', top: '0', zIndex: '1', backgroundColor: '#333' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #525252', borderTop: '1px solid #525252', textAlign: 'left' }}>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Symbol</th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Rank</th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Price (USD)</th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Chg (%)</th>
                </tr>
              </thead>
            </table>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {spinList && (
                  <>                   
                    <br />
                    <Spinner title="" color="#0e440d" />
                  </>
                )}
                {!spinList && filteredSymbols.map((symbol, index) => (
                  prices[symbol] !== null && getPercentageChange(symbol.toUpperCase()) !== 'isNaN' && (
                    <tr key={symbol} className={styles.table_row} style={{ borderBottom: '1px solid #525252', backgroundColor: selectedRowIndex === index ? '#0096ff96' : '' }} onClick={() => handleSelection(index, symbol)}>
                      <td style={{ verticalAlign: 'middle', display: 'flex', alignItems: 'center' }}>
                        <img
                          src={symbolIcons[symbol]}
                          alt=""
                          style={{ width: '20px', marginRight: '10px', borderRadius: '50%' }} />
                        {symbol.toUpperCase().slice(0, -4)}
                      </td>
                      <td style={{ textAlign: 'left' }}>{index + 1}</td>
                      <td style={{ color: getColor(getPercentageChange(symbol.toUpperCase())), width:'38%' }}>
                        {prices[symbol] !== null ? prices[symbol] : 'Loading...'}
                      </td>
                      <td style={{ color: getColor(getPercentageChange(symbol.toUpperCase())), width: '70px' }}>
                        {prices[symbol] !== null && initialPrices[symbol] !== null && (
                          <>
                            {getPercentageChange(symbol.toUpperCase())}%
                          </>
                        )}
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <fieldset className={styles.fieldset} style={{fontSize:'12px'}}>
            <legend className={styles.legend}>Quick glance on EMAs and Intervals</legend>
            <EmaSelector />
        </fieldset>
        </>
    {/* } */}
      </>
  );
};

export default StreamCryptoCInfo;

